import type { ExerciseTemplate } from '@yuan-math/exercises';
import { roundTo } from '@yuan-math/math';
import { choiceExercise, numericExercise, options, template, trap } from './authoring.ts';

/**
 * Proportions, proportional relationships and percent in use.
 *
 * Almost every wrong answer in this area comes from one habit: comparing by
 * difference when the situation compares by multiplication. The traps below
 * name that habit rather than simply marking the answer wrong.
 *
 * The `proportional-relationships` templates add a second habit worth naming —
 * finding k from a single row and never checking it against another, which is
 * how a joining fee or a call-out charge goes unnoticed.
 */
export const PROPORTION_TEMPLATES: ExerciseTemplate[] = [
  template({
    id: 'proportion-solve-missing',
    conceptId: 'proportions',
    description: 'Find the missing term in a proportion.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const a = rng.int(2, 9);
      const b = rng.pick([2, 3, 4, 5, 6, 7, 8, 9].filter((n) => n !== a));
      const k = rng.int(2, difficulty === 4 ? 12 : 6);
      const c = a * k;
      const answer = b * k;

      return numericExercise({
        prompt: 'Find the value of x.',
        latex: `\\frac{${a}}{${b}} = \\frac{${c}}{x}`,
        value: answer,
        summary: `The top was multiplied by ${k}, so the bottom must be too: ${b} × ${k} = ${answer}.`,
        hint: `What do you multiply ${a} by to get ${c}?`,
        steps: [
          { label: 'Find the multiplier between the two numerators.', latex: `${c} \\div ${a} = ${k}` },
          { label: 'Apply the same multiplier to the denominator.', latex: `${b} \\times ${k} = ${answer}` },
          { label: 'Check by simplifying the second fraction.', latex: `\\frac{${c}}{${answer}} = \\frac{${a}}{${b}}` },
        ],
        // Adding the difference instead of applying the multiplier.
        traps: [trap(b + (c - a), 'proportion-additive')],
      });
    },
  }),

  template({
    id: 'proportion-word-scale',
    conceptId: 'proportions',
    description: 'Scale a quantity in a word problem.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const per = rng.int(2, difficulty === 4 ? 15 : 8);
      const few = rng.int(2, 6);
      const many = few * rng.int(2, 5);
      const cost = per * few;
      const answer = per * many;
      const scenario = rng.pick([
        { prompt: `${few} notebooks cost £${cost}. At the same price, what do ${many} notebooks cost?`, unit: '£' },
        { prompt: `A machine fills ${cost} bottles in ${few} minutes. How many does it fill in ${many} minutes?`, unit: 'bottles' },
        { prompt: `${few} litres of paint cover ${cost} square metres. How many square metres will ${many} litres cover?`, unit: 'square metres' },
      ]);

      return numericExercise({
        prompt: scenario.prompt,
        unit: scenario.unit,
        value: answer,
        summary: `${many} is ${many / few} times ${few}, so the answer is ${cost} × ${many / few} = ${answer}.`,
        hint: 'How many times bigger is the second amount than the first? Do the same to the other quantity.',
        steps: [
          { label: 'Find the scale factor.', latex: `${many} \\div ${few} = ${many / few}` },
          { label: 'Scale the other quantity by the same amount.', latex: `${cost} \\times ${many / few} = ${answer}` },
        ],
        traps: [trap(cost + (many - few), 'proportion-additive')],
      });
    },
  }),

  template({
    id: 'proportion-identify',
    conceptId: 'proportions',
    description: 'Decide which pair of ratios actually forms a proportion.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const a = rng.int(2, 9);
      const b = rng.pick([2, 3, 4, 5, 6, 7, 8, 9].filter((n) => n !== a));
      const k = rng.int(2, 5);
      const shift = rng.int(1, 5);

      const { choices, correctId } = options(
        [
          { latex: `\\frac{${a}}{${b}} = \\frac{${a * k}}{${b * k}}`, correct: true },
          { latex: `\\frac{${a}}{${b}} = \\frac{${a + shift}}{${b + shift}}`, misconceptionId: 'proportion-additive' },
          { latex: `\\frac{${a}}{${b}} = \\frac{${a * k}}{${b + k}}`, misconceptionId: 'proportion-additive' },
          { latex: `\\frac{${a}}{${b}} = \\frac{${b * k}}{${a * k}}`, misconceptionId: 'divide-wrong-direction' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Which of these is a true proportion?',
        choices,
        correctId,
        summary: `Multiplying the top and the bottom by the same number leaves the ratio unchanged, so ${a}:${b} = ${a * k}:${b * k}.`,
        hint: 'A proportion survives multiplication, not addition. Try scaling each one and see which holds.',
      });
    },
  }),

  /* ------------------------------------------- proportional relationships */
  template({
    id: 'proportional-constant-from-table',
    conceptId: 'proportional-relationships',
    description: 'Read the constant of proportionality out of a table.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const k = rng.int(2, difficulty === 3 ? 15 : 9);
      const [x1, x2, x3] = rng.shuffle([2, 3, 4, 5, 6, 8, 9, 10]).slice(0, 3) as [number, number, number];
      const scenario = rng.pick([
        { x: 'Hours', y: 'Pages typed' },
        { x: 'Litres', y: 'Kilometres driven' },
        { x: 'Bags', y: 'Total cost (£)' },
        { x: 'Minutes', y: 'Words written' },
      ]);

      return numericExercise({
        prompt: `${scenario.x} (x): ${x1}, ${x2}, ${x3}. ${scenario.y} (y): ${k * x1}, ${k * x2}, ${k * x3}. The relationship is proportional — find the constant of proportionality k, where y = kx.`,
        value: k,
        summary: `Every row gives the same answer: ${k * x1} ÷ ${x1} = ${k}. So y = ${k}x.`,
        hint: 'Divide a y value by the x value beside it — and check that another row agrees.',
        steps: [
          { label: 'Divide y by x on the first row.', latex: `\\frac{${k * x1}}{${x1}} = ${k}` },
          { label: 'Check the next row gives the same.', latex: `\\frac{${k * x2}}{${x2}} = ${k}` },
          { label: 'Write the equation.', latex: `y = ${k}x` },
        ],
        traps: [
          // x ÷ y rather than y ÷ x. Only distinguishable when k ≠ 1.
          trap(roundTo(x1 / (k * x1), 4), 'constant-of-proportionality-inverted'),
          // Comparing the row by difference rather than by division. Skipped
          // when the difference happens to land on k itself.
          ...(k * x1 - x1 === k ? [] : [trap(k * x1 - x1, 'proportion-additive')]),
        ],
      });
    },
  }),

  template({
    id: 'proportional-predict-from-k',
    conceptId: 'proportional-relationships',
    description: 'Use y = kx to predict a value the table does not show.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const k = rng.int(2, difficulty === 4 ? 14 : 8);
      const known = rng.int(2, 9);
      const wanted = rng.int(11, difficulty === 4 ? 40 : 20);
      const scenario = rng.pick([
        { prompt: `A printer prints ${k * known} pages in ${known} minutes at a steady rate. How many pages does it print in ${wanted} minutes?`, unit: 'pages' },
        { prompt: `${known} identical crates weigh ${k * known} kg. How much do ${wanted} of them weigh?`, unit: 'kg' },
        { prompt: `A car covers ${k * known} km on ${known} litres of fuel. How far does it go on ${wanted} litres?`, unit: 'km' },
      ]);

      return numericExercise({
        prompt: scenario.prompt,
        unit: scenario.unit,
        value: k * wanted,
        summary: `The constant of proportionality is ${k * known} ÷ ${known} = ${k}, so y = ${k}x and ${wanted} gives ${k * wanted}.`,
        hint: 'Find the amount for one first. That single number answers every version of the question.',
        steps: [
          { label: 'Find k, the amount per one.', latex: `k = \\frac{${k * known}}{${known}} = ${k}` },
          { label: 'Apply it.', latex: `y = ${k} \\times ${wanted} = ${k * wanted}` },
        ],
        traps: [
          // Adding the difference rather than scaling — the Grade 6 habit.
          trap(k * known + (wanted - known), 'proportion-additive'),
          // k taken as x ÷ y, so the prediction divides instead of multiplying.
          trap(roundTo(wanted / k, 4), 'constant-of-proportionality-inverted'),
        ],
      });
    },
  }),

  template({
    id: 'proportional-identify-relationship',
    conceptId: 'proportional-relationships',
    description: 'Decide which of four relationships is proportional.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const k = rng.int(2, 9);
      const fee = rng.int(2, 12);

      const { choices, correctId } = options(
        [
          { latex: `y = ${k}x`, correct: true },
          { latex: `y = ${k}x + ${fee}`, misconceptionId: 'nonproportional-taken-as-proportional' },
          { latex: `y = x + ${k}`, misconceptionId: 'proportion-additive' },
          { latex: `y = \\frac{x}{${k}} + ${fee}`, misconceptionId: 'nonproportional-taken-as-proportional' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Which of these is a proportional relationship?',
        choices,
        correctId,
        summary: `Only y = ${k}x has nothing added on. At x = 0 it gives y = 0, and doubling x doubles y — which the others do not.`,
        hint: 'Put x = 0 into each one. A proportional relationship must give y = 0.',
        steps: [
          { label: 'A proportional relationship has the form y = kx.', latex: 'y = kx' },
          { label: 'Anything added on breaks it.', latex: `y = ${k}x + ${fee} \\Rightarrow y = ${fee}\\text{ at }x = 0` },
        ],
      });
    },
  }),

  template({
    id: 'proportional-from-graph',
    conceptId: 'proportional-relationships',
    description: 'Read the constant of proportionality off a straight-line graph through the origin.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const k = rng.pick([2, 3, 4, 5, 6, 7, 8]);
      const at = rng.int(2, 5);

      return numericExercise({
        prompt: 'The line on this graph passes through the origin. What is its constant of proportionality?',
        figure: {
          kind: 'coordinate-grid',
          min: 0,
          max: 10,
          lines: [{ slope: k, intercept: 0 }],
          points: [{ x: at, y: k * at, label: `(${at}, ${k * at})` }],
        },
        value: k,
        summary: `The marked point is (${at}, ${k * at}), so k = ${k * at} ÷ ${at} = ${k}. The line is y = ${k}x.`,
        hint: 'Take any point on the line and divide its height by its distance across.',
        steps: [
          { label: 'Read a point off the line.', latex: `(${at},\\; ${k * at})` },
          { label: 'Divide y by x.', latex: `k = \\frac{${k * at}}{${at}} = ${k}` },
        ],
        traps: [
          trap(roundTo(at / (k * at), 4), 'constant-of-proportionality-inverted'),
          ...(k * at - at === k ? [] : [trap(k * at - at, 'proportion-additive')]),
        ],
      });
    },
  }),

  template({
    id: 'percent-change-result',
    conceptId: 'percent-applications',
    description: 'Apply a percentage increase or decrease and give the resulting amount.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const base = rng.int(2, difficulty === 4 ? 25 : 10) * 20;
      const increase = rng.bool();
      // A 50% fall halves the amount, which would make "the change" and "the
      // result" the same number and the first trap indistinguishable from a
      // correct answer. Left out rather than left to the safety net.
      const rates = increase ? [5, 10, 15, 20, 25, 40, 50] : [5, 10, 15, 20, 25, 40];
      const pct = rng.pick(rates);
      const change = (base * pct) / 100;
      const answer = increase ? base + change : base - change;
      const scenario = increase
        ? { prompt: `A £${base} bicycle goes up in price by ${pct}%. What does it cost now?`, unit: '£' }
        : { prompt: `A £${base} coat is reduced by ${pct}% in a sale. What does it cost now?`, unit: '£' };

      return numericExercise({
        prompt: scenario.prompt,
        unit: scenario.unit,
        value: answer,
        summary: `${pct}% of £${base} is £${change}, so the new price is £${base} ${increase ? '+' : '−'} £${change} = £${answer}.`,
        hint: 'Work out the size of the change first — then remember the question asks for the price, not the change.',
        steps: [
          { label: `Find ${pct}% of the original price.`, latex: `\\frac{${pct}}{100} \\times ${base} = ${change}` },
          { label: `${increase ? 'Add it on' : 'Take it off'}.`, latex: `${base} ${increase ? '+' : '-'} ${change} = ${answer}` },
        ],
        traps: [
          trap(change, 'percent-change-not-applied'),
          // Treating the percent as a number of pounds.
          ...(base === 100 ? [] : [trap(increase ? base + pct : base - pct, 'percent-not-converted')]),
        ],
      });
    },
  }),

  template({
    id: 'percent-change-rate',
    conceptId: 'percent-applications',
    description: 'Work backwards from two amounts to the percentage change.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const original = rng.int(2, 20) * 20;
      const pct = rng.pick([10, 20, 25, 50, 75]);
      const increase = rng.bool();
      const change = (original * pct) / 100;
      const now = increase ? original + change : original - change;

      return numericExercise({
        prompt: `A price ${increase ? 'rose' : 'fell'} from £${original} to £${now}. By what percentage did it change?`,
        unit: '%',
        value: pct,
        summary: `The change was £${change}. As a fraction of the *original* £${original} that is ${change}/${original} = ${pct}%.`,
        hint: 'Percentage change is always measured against where you started, not where you ended up.',
        steps: [
          { label: 'Find the size of the change.', latex: `${Math.abs(now - original)}` },
          { label: 'Divide by the original amount.', latex: `\\frac{${change}}{${original}}` },
          { label: 'Turn it into a percentage.', latex: `${pct}\\%` },
        ],
        traps: [trap(roundTo((change / now) * 100, 2), 'percent-change-wrong-base')],
      });
    },
  }),

  template({
    id: 'percent-simple-interest',
    conceptId: 'percent-applications',
    description: 'Compute simple interest and the balance it leaves.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const principal = rng.int(2, 20) * 100;
      const rate = rng.pick([2, 3, 4, 5, 6, 8, 10]);
      const years = rng.int(2, 6);
      const interest = (principal * rate * years) / 100;
      const answer = principal + interest;

      return numericExercise({
        prompt: `£${principal} is saved at ${rate}% simple interest per year. How much is in the account after ${years} years?`,
        unit: '£',
        value: answer,
        summary: `Interest is £${(principal * rate) / 100} a year for ${years} years, so £${interest} in total — leaving £${answer}.`,
        hint: 'Simple interest is earned on the original amount every year. Work out one year, then multiply.',
        steps: [
          { label: 'One year of interest.', latex: `\\frac{${rate}}{100} \\times ${principal} = ${(principal * rate) / 100}` },
          { label: `Over ${years} years.`, latex: `${(principal * rate) / 100} \\times ${years} = ${interest}` },
          { label: 'Add it to what was saved.', latex: `${principal} + ${interest} = ${answer}` },
        ],
        traps: [
          trap(interest, 'percent-change-not-applied'),
          // One year's interest, with the time forgotten entirely.
          trap(principal + (principal * rate) / 100, 'percent-change-not-applied'),
        ],
      });
    },
  }),
];
